import {
  EquipmentState,
  getV1CompaniesIdEquipment,
  patchV1CompaniesCompanyIdEquipmentId,
  postV1CompaniesCompanyIdEquipment,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { Equipment } from '~hooks/useEquipment/models';
import { CSVColumn } from '~utils/csv/csvProcessor';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { EntityCSVConfig } from './types';

const equipmentColumns: CSVColumn[] = [
  {
    key: 'id',
    header: 'id',
    required: false,
    exampleValue: '',
    description: 'Existing ID (for updates only)',
  },
  {
    key: 'name',
    header: 'name',
    required: true,
    exampleValue: 'Truck 101',
    description: 'Equipment name/number (e.g., Truck 101)',
  },
  {
    key: 'type',
    header: 'type',
    required: true,
    exampleValue: 'Dump Truck',
    description: 'Equipment type (MUST exist - create types first)',
  },
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'EQ-001',
    description: 'Your internal reference ID',
  },
  {
    key: 'license_number',
    header: 'license_number',
    required: false,
    exampleValue: 'ABC-1234',
    description: 'Vehicle license plate',
  },
  {
    key: 'accounting_id',
    header: 'accounting_id',
    required: false,
    exampleValue: 'ACCT-300',
    description: 'Accounting system reference',
  },
  {
    key: 'zone',
    header: 'zone',
    required: false,
    exampleValue: 'Zone A',
    description: 'Zone assignment',
  },
  {
    key: 'status',
    header: 'status',
    required: false,
    enumValues: ['ACTIVE', 'INACTIVE'],
    exampleValue: 'ACTIVE',
    description: 'ACTIVE or INACTIVE',
  },
  {
    key: 'internal_to',
    header: 'internal_to',
    required: false,
    exampleValue: '',
    description:
      'Connected company this becomes internal to (blank means your own company). New equipment only — permanent once set.',
  },
  {
    key: 'share_name',
    header: 'share_name',
    required: false,
    exampleValue: '',
    description: "The receiving company's own label for it. New equipment only.",
  },
  {
    key: 'share_ticket_match_id',
    header: 'share_ticket_match_id',
    required: false,
    exampleValue: '',
    description: "The receiving company's ticket match ID. New equipment only.",
  },
];

interface EquipmentConfigResolvers {
  getEquipmentTypeIdByName: (name: string) => string | undefined;
  getConnectedCompanyIdByName: (name: string) => string | undefined;
  getConnectedCompanyNameById: (companyId: string) => string | undefined;
}

const resolveInternalToCompanyId = (
  internalTo: string | undefined,
  { getConnectedCompanyIdByName }: EquipmentConfigResolvers,
): string | undefined => {
  const name = internalTo?.trim();
  if (!name) {
    return undefined;
  }

  const companyId = getConnectedCompanyIdByName(name);
  if (!companyId) {
    throw new Error(
      t('csv_import_export.errors.invalid_reference', {
        field: 'internal_to',
        value: name,
      }),
    );
  }

  return companyId;
};

export const createEquipmentConfig = (
  resolvers: EquipmentConfigResolvers,
  scopedCompanyId: string,
): EntityCSVConfig<Equipment> => ({
  entityType: 'equipment',
  columns: equipmentColumns,

  fetchAll: (companyId: string) =>
    fetchAllApiPages(
      (query) => getV1CompaniesIdEquipment({ path: { 'company-id': companyId }, query }),
      Equipment.parse,
    ),
  shouldLoadConfigBeforeExport: true,

  create: async (companyId: string, data: Record<string, string>): Promise<Equipment> => {
    const typeId = resolvers.getEquipmentTypeIdByName(data.type);
    if (!typeId) {
      throw new Error(
        t('csv_import_export.errors.invalid_reference', {
          field: 'type',
          value: data.type,
        }),
      );
    }

    const response = await postV1CompaniesCompanyIdEquipment({
      path: { 'company-id': companyId },
      body: {
        name: data.name?.trim(),
        equipment_type_id: typeId,
        external_id: data.external_id?.trim() || undefined,
        license_number: data.license_number?.trim() || undefined,
        accounting_id: data.accounting_id?.trim() || undefined,
        zone: data.zone?.trim() || undefined,
        state:
          data.status?.toUpperCase() === 'INACTIVE'
            ? EquipmentState.INACTIVE
            : EquipmentState.ACTIVE,
        provision_for_company_id: resolveInternalToCompanyId(data.internal_to, resolvers),
        share_name: data.share_name?.trim() || undefined,
        share_ticket_match_id: data.share_ticket_match_id?.trim() || undefined,
      },
    });

    return Equipment.parse(response.data.data);
  },

  update: async (id: string, data: Record<string, string>): Promise<Equipment> => {
    const typeId = data.type ? resolvers.getEquipmentTypeIdByName(data.type) : undefined;
    if (data.type && !typeId) {
      throw new Error(
        t('csv_import_export.errors.invalid_reference', {
          field: 'type',
          value: data.type,
        }),
      );
    }

    // internal_to/share_name/share_ticket_match_id are write-once and only settable at
    // creation — an update row can't safely re-resolve or re-send them without risking a
    // 422 against equipment that's already internal to someone.
    const response = await patchV1CompaniesCompanyIdEquipmentId({
      path: { 'company-id': scopedCompanyId, id },
      body: {
        name: data.name?.trim() || undefined,
        equipment_type_id: typeId,
        external_id: data.external_id?.trim() || null,
        license_number: data.license_number?.trim() || null,
        accounting_id: data.accounting_id?.trim() || null,
        zone: data.zone?.trim() || null,
        state:
          data.status?.toUpperCase() === 'INACTIVE'
            ? EquipmentState.INACTIVE
            : EquipmentState.ACTIVE,
      },
    });

    return Equipment.parse(response.data.data);
  },

  transformExport: (equipment: Equipment): Record<string, string> => ({
    id: equipment.id,
    name: equipment.name,
    type: equipment.type?.name || '',
    external_id: equipment.externalId || '',
    license_number: equipment.licenseNumber || '',
    accounting_id: equipment.accountingId || '',
    zone: equipment.zone || '',
    status: equipment.isNotActive ? 'INACTIVE' : 'ACTIVE',
    // resolved through the same connected-accounts data import matches against, so a
    // round-tripped export always resolves — equipment.internalCompany.legalName can drift
    // from the connected account's own name if the company renamed itself since connecting
    internal_to: equipment.internalCompany
      ? (resolvers.getConnectedCompanyNameById(equipment.internalCompany.id) ??
        equipment.internalCompany.legalName)
      : '',
    share_name: '',
    share_ticket_match_id: '',
  }),

  transformImport: (row: Record<string, string>): Record<string, unknown> => ({
    name: row.name?.trim(),
    type: row.type?.trim(),
    external_id: row.external_id?.trim() || null,
    license_number: row.license_number?.trim() || null,
    accounting_id: row.accounting_id?.trim() || null,
    zone: row.zone?.trim() || null,
    status: row.status?.toUpperCase() || 'ACTIVE',
    internal_to: row.internal_to?.trim() || '',
    share_name: row.share_name?.trim() || '',
    share_ticket_match_id: row.share_ticket_match_id?.trim() || '',
  }),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    // Required fields
    if (!row.name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'name' }));
    }
    if (!row.type?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'type' }));
    }

    // Enum validation
    if (row.status && !['ACTIVE', 'INACTIVE'].includes(row.status.toUpperCase())) {
      errors.push(
        t('csv_import_export.errors.invalid_enum', {
          field: 'status',
          values: 'ACTIVE, INACTIVE',
        }),
      );
    }

    if (row.internal_to && !row.id) {
      try {
        resolveInternalToCompanyId(row.internal_to, resolvers);
      } catch (error) {
        errors.push((error as Error).message);
      }
    }

    return errors;
  },
});
